import axios, { AxiosError } from "axios";
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  CancelToken,
} from "axios";

export class TranslationClient {
  protected instance: AxiosInstance;
  protected baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(baseUrl?: string, instance?: AxiosInstance) {
    this.instance = instance || axios.create();
    this.instance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("accessToken");
        if (token) {
          if (!config.headers) {
            config.headers = {};
          }
          config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
    this.baseUrl = baseUrl ?? "";
  }

  static(key: string, language: number, cancelToken?: CancelToken): Promise<string> {
    let url_ = this.baseUrl + "/api/translation/static/{key}/{language}";
    if (key === undefined || key === null)
        throw new Error("The parameter 'key' must be defined.");
    url_ = url_.replace("{key}", encodeURIComponent("" + key));
    if (language === undefined || language === null)
        throw new Error("The parameter 'language' must be defined.");
    url_ = url_.replace("{language}", encodeURIComponent("" + language));
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
        method: "GET",
        url: url_,
        headers: {},
        cancelToken
    };

    return this.instance.request(options_).then((_response: AxiosResponse) => {
        return this.processStatic(_response);
    });
}

protected processStatic(response: AxiosResponse): Promise<string> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
        for (const k in response.headers) {
            if (response.headers.hasOwnProperty(k)) {
                _headers[k] = response.headers[k];
            }
        }
    }

    if (status === 200) {
        const _responseText = response.data;
        if (typeof _responseText === "string") {
            return Promise.resolve(_responseText);
        } else if (_responseText && _responseText.translation) {
            return Promise.resolve(_responseText.translation);
        } else {
            return Promise.resolve(""); // Возвращаем пустую строку, если перевод не найден
        }
    } else if (status === 500) {
        return throwException("Internal Server Error", status, response.data, _headers);
    } else {
        return throwException("An unexpected server error occurred.", status, response.data, _headers);
    }
}


  addkey(
    pTranslation: string,
    languageId: number,
    body?: StaticTranslationDTO | undefined,
    cancelToken?: CancelToken
  ): Promise<void> {
    let url_ =
      this.baseUrl + "/api/translation/addkey/{pTranslation}/{languageId}";
    if (pTranslation === undefined || pTranslation === null)
      throw new Error("The parameter 'pTranslation' must be defined.");
    url_ = url_.replace(
      "{pTranslation}",
      encodeURIComponent("" + pTranslation)
    );
    if (languageId === undefined || languageId === null)
      throw new Error("The parameter 'languageId' must be defined.");
    url_ = url_.replace("{languageId}", encodeURIComponent("" + languageId));
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken,
    };

    return this.instance
      .request(options_)
      .catch((_error: any) => {
        if (isAxiosError(_error) && _error.response) {
          return _error.response;
        } else {
          throw _error;
        }
      })
      .then((_response: AxiosResponse) => {
        return this.processAddkey(_response);
      });
  }

  protected processAddkey(response: AxiosResponse): Promise<void> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 500) {
      const _responseText = response.data;
      return throwException(
        "Internal Server Error",
        status,
        _responseText,
        _headers
      );
    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException(
        "An unexpected server error occurred.",
        status,
        _responseText,
        _headers
      );
    }
    return Promise.resolve<void>(null as any);
  }

  updatekey(
    body?: StaticTranslationDTO | undefined,
    cancelToken?: CancelToken
  ): Promise<void> {
    let url_ = this.baseUrl + "/api/translation/updatekey";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken,
    };

    return this.instance
      .request(options_)
      .catch((_error: any) => {
        if (isAxiosError(_error) && _error.response) {
          return _error.response;
        } else {
          throw _error;
        }
      })
      .then((_response: AxiosResponse) => {
        return this.processUpdatekey(_response);
      });
  }

  protected processUpdatekey(response: AxiosResponse): Promise<void> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 500) {
      const _responseText = response.data;
      return throwException(
        "Internal Server Error",
        status,
        _responseText,
        _headers
      );
    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException(
        "An unexpected server error occurred.",
        status,
        _responseText,
        _headers
      );
    }
    return Promise.resolve<void>(null as any);
  }

  list(
    body?: GetTranslationsListRequest | undefined,
    cancelToken?: CancelToken
  ): Promise<StaticTranslationDTO[]> {
    let url_ = this.baseUrl + "/api/translation/list";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken,
    };

    return this.instance.request(options_).then((_response: AxiosResponse) => {
      return this.processList(_response);
    });
  }

  protected processList(
    response: AxiosResponse
  ): Promise<StaticTranslationDTO[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }

    if (status === 200) {
      const _responseText = response.data;
      return Promise.resolve(
        _responseText.map((item: any) => StaticTranslationDTO.fromJS(item))
      );
    } else if (status === 500) {
      return throwException(
        "Internal Server Error",
        status,
        response.data,
        _headers
      );
    } else {
      return throwException(
        "An unexpected server error occurred.",
        status,
        response.data,
        _headers
      );
    }
  }
}

export class GetTranslationsListRequest implements IGetTranslationsListRequest {
  pageNumber?: number;
  pageSize?: number;
  languageId?: number;
  module?: string | null;
  key?: string | null;

  constructor(data?: IGetTranslationsListRequest) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.pageNumber =
        _data["pageNumber"] !== undefined ? _data["pageNumber"] : <any>null;
      this.pageSize =
        _data["pageSize"] !== undefined ? _data["pageSize"] : <any>null;
      this.languageId =
        _data["languageId"] !== undefined ? _data["languageId"] : <any>null;
      this.module = _data["module"] !== undefined ? _data["module"] : <any>null;
      this.key = _data["key"] !== undefined ? _data["key"] : <any>null;
    }
  }

  static fromJS(data: any): GetTranslationsListRequest {
    data = typeof data === "object" ? data : {};
    let result = new GetTranslationsListRequest();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["pageNumber"] =
      this.pageNumber !== undefined ? this.pageNumber : <any>null;
    data["pageSize"] = this.pageSize !== undefined ? this.pageSize : <any>null;
    data["languageId"] =
      this.languageId !== undefined ? this.languageId : <any>null;
    data["module"] = this.module !== undefined ? this.module : <any>null;
    data["key"] = this.key !== undefined ? this.key : <any>null;
    return data;
  }
}

export interface IGetTranslationsListRequest {
  pageNumber?: number;
  pageSize?: number;
  languageId?: number;
  module?: string | null;
  key?: string | null;
}

export class StaticTranslationDTO implements IStaticTranslationDTO {
  id?: number;
  writeRevision?: boolean | null;
  key?: string | null;
  module?: string | null;
  description?: string | null;
  url?: string | null;
  translations?: TranslatedValueDTO[] | null;

  constructor(data?: IStaticTranslationDTO) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"] !== undefined ? _data["id"] : <any>null;
      this.writeRevision =
        _data["writeRevision"] !== undefined
          ? _data["writeRevision"]
          : <any>null;
      this.key = _data["key"] !== undefined ? _data["key"] : <any>null;
      this.module = _data["module"] !== undefined ? _data["module"] : <any>null;
      this.description =
        _data["description"] !== undefined ? _data["description"] : <any>null;
      this.url = _data["url"] !== undefined ? _data["url"] : <any>null;
      if (Array.isArray(_data["translations"])) {
        this.translations = [] as any;
        for (let item of _data["translations"])
          this.translations!.push(TranslatedValueDTO.fromJS(item));
      } else {
        this.translations = <any>null;
      }
    }
  }

  static fromJS(data: any): StaticTranslationDTO {
    data = typeof data === "object" ? data : {};
    let result = new StaticTranslationDTO();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id !== undefined ? this.id : <any>null;
    data["writeRevision"] =
      this.writeRevision !== undefined ? this.writeRevision : <any>null;
    data["key"] = this.key !== undefined ? this.key : <any>null;
    data["module"] = this.module !== undefined ? this.module : <any>null;
    data["description"] =
      this.description !== undefined ? this.description : <any>null;
    data["url"] = this.url !== undefined ? this.url : <any>null;
    if (Array.isArray(this.translations)) {
      data["translations"] = [];
      for (let item of this.translations)
        data["translations"].push(item.toJSON());
    }
    return data;
  }
}

export interface IStaticTranslationDTO {
  id?: number;
  writeRevision?: boolean | null;
  key?: string | null;
  module?: string | null;
  description?: string | null;
  url?: string | null;
  translations?: TranslatedValueDTO[] | null;
}

export class TranslatedValueDTO implements ITranslatedValueDTO {
  id?: number;
  writeRevision?: boolean | null;
  keyId?: number;
  languageId?: number;
  translation?: string | null;

  constructor(data?: ITranslatedValueDTO) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"] !== undefined ? _data["id"] : <any>null;
      this.writeRevision =
        _data["writeRevision"] !== undefined
          ? _data["writeRevision"]
          : <any>null;
      this.keyId = _data["keyId"] !== undefined ? _data["keyId"] : <any>null;
      this.languageId =
        _data["languageId"] !== undefined ? _data["languageId"] : <any>null;
      this.translation =
        _data["translation"] !== undefined ? _data["translation"] : <any>null;
    }
  }

  static fromJS(data: any): TranslatedValueDTO {
    data = typeof data === "object" ? data : {};
    let result = new TranslatedValueDTO();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id !== undefined ? this.id : <any>null;
    data["writeRevision"] =
      this.writeRevision !== undefined ? this.writeRevision : <any>null;
    data["keyId"] = this.keyId !== undefined ? this.keyId : <any>null;
    data["languageId"] =
      this.languageId !== undefined ? this.languageId : <any>null;
    data["translation"] =
      this.translation !== undefined ? this.translation : <any>null;
    return data;
  }
}

export interface ITranslatedValueDTO {
  id?: number;
  writeRevision?: boolean | null;
  keyId?: number;
  languageId?: number;
  translation?: string | null;
}

export class ApiException extends Error {
  override message: string;
  status: number;
  response: string;
  headers: { [key: string]: any };
  result: any;

  constructor(
    message: string,
    status: number,
    response: string,
    headers: { [key: string]: any },
    result: any
  ) {
    super();

    this.message = message;
    this.status = status;
    this.response = response;
    this.headers = headers;
    this.result = result;
  }

  protected isApiException = true;

  static isApiException(obj: any): obj is ApiException {
    return obj.isApiException === true;
  }
}

function throwException(
  message: string,
  status: number,
  response: string,
  headers: { [key: string]: any },
  result?: any
): any {
  if (result !== null && result !== undefined) throw result;
  else throw new ApiException(message, status, response, headers, null);
}

function isAxiosError(obj: any): obj is AxiosError {
  return obj && obj.isAxiosError === true;
}
